<template>
  <v-container fluid class="pa-4">
    <v-card elevation="1" width="100%">
      <v-card-title>
        <v-switch
            inset
            v-model="currentConfig.is24HourDateTime"
            color="secondary"
            :label="`24 ${ $t('misc.hourFormat') }`"
        />
      </v-card-title>
      <v-select
        :items="locales"
        v-model="currentConfig.locale"
        :disabled="locales.length <= 1"
        filled
        :label="`${ $t('locale.language') }`"
        :loading="loading"
        persistent-hint
        class="pa-4"
      >
        <template v-slot:selection="{ item }">
          {{ $t(`locale.${item}`) }}
        </template>

        <template v-slot:item="{ item }">
          {{ $t(`locale.${item}`) }}
        </template>
      </v-select>
    </v-card>

    <v-footer absolute elevation="2" class="py-3" color="white">
      <v-btn outlined color="primary" @click="$router.back()">{{ $t('Navigation.back') }}</v-btn>
      <v-spacer/>
      <v-btn
        color="primary"
        :loading="savingConfig"
        :disabled="!hasChanges"
        @click="submitConfiguration"
      >
        {{ $t('actions.save') }}
      </v-btn>
    </v-footer>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import hasAppSnackbar from '@/mixins/hasAppSnackbar';
import hasAppDialog from '@/mixins/hasAppDialog';
import resolveMobileBackButton from '@/mixins/resolveMobileBackButton';
import constants from '@/constants';

export default {
  name: 'Settings',
  mixins: [hasAppDialog, hasAppSnackbar, resolveMobileBackButton],
  data() {
    return {
      currentConfig: Object,
      savingConfig: false,
      loading: false,
      pendingNav: Function,
      confirmText: '',
      cancelText: '',
    };
  },

  computed: {
    ...mapState({
      userConfiguration: (state) => state.userConfiguration,
      locales: (state) => state.locales,
    }),
    hasChanges() {
      return !isEqual(this.currentConfig, this.userConfiguration);
    },
  },

  methods: {
    ...mapMutations(['setPageTitle']),
    ...mapActions([
      'saveUserConfiguration',
      'loadSupportedLocales',
      'fetchNotifications',
      'loadCorrectionReasons',
      'updateUserAssignments',
    ]),

    submitConfiguration() {
      this.showAppConfirmDialog({
        message: this.$t('dialogs.confirm.updateSettings'),
        confirmHandler: this.saveConfiguration,
      });
    },

    saveConfiguration() {
      this.savingConfig = true;
      this.saveUserConfiguration(this.currentConfig)
      .then(() => {
        this.changeLocale(this.currentConfig.locale);
        this.showSnackbar({
          text: this.$t('snackbar.configUpdated'),
          color: constants.AppColors.PRIMARY,
        });
      })
      .finally(() => {
        this.savingConfig = false;
      });
    },

    confirmSaveBeforeNav() {
      this.saveConfiguration();
      this.pendingNav();
    },

    changeLocale(locale) {
      if (this.$i18n.locale !== locale) {
        this.$i18n.locale = locale;
        this.fetchNotifications();
        this.loadCorrectionReasons();
        this.updateUserAssignments(this.currentConfig.user);
        this.setPageTitle(this.$t('Navigation.settings'));
      }
    },
  },

  created() {
    this.loading = true;
    this.setPageTitle(this.$t('Navigation.settings'));
    this.loadSupportedLocales().finally(() => {
      this.loading = false;
    });
    this.currentConfig = cloneDeep(this.userConfiguration);
    if (!this.currentConfig.locale) {
      this.currentConfig.locale = 'en';
    }
  },

  beforeRouteLeave(to, from, next) {
    if (!isEqual(this.currentConfig, this.userConfiguration)) {
      this.pendingNav = next;
      this.showAppConfirmDialog({
        message: this.$t('dialogs.confirm.unsavedChanges'),
        confirmText: this.$t('actions.saveChanges'),
        confirmHandler: this.confirmSaveBeforeNav,
        cancelText: this.$t('actions.discardChanges'),
        cancelHandler: this.pendingNav,
      });
    } else {
      next();
    }
  },
};

</script>
